import React, { useState } from 'react';
import { useClientTokenManager } from '@cko/dashboard-shared';
import {
  Box,
  Input,
  SearchBox,
  SecondaryButton,
  TextHeadingThree,
  TextHeadingTwo,
} from '@cko/primitives';
import { Permission } from '../../../../../config/shared/enums/permissions';
import { useDeveloperToolsProvider } from '../../../../context';
import { filterBySearch } from '../utils';
import { PermissionsOverrides } from '../../../../types';
import { GridViewContent, GridView } from '../../../GridView';
import { PermissionToggle } from './PermissionToggle';

const getCurrentPermissions = (
  showOverridesOnly: boolean,
  tokenPermissions: string[],
  overrides: PermissionsOverrides,
) => {
  if (showOverridesOnly) {
    return overrides;
  }
  const knownPermissionEntries = Object.values(Permission).map((permission) => [
    String(permission),
    false,
  ]);
  const tokenPermissionEntries = tokenPermissions.map((permission) => [permission, true]);
  const basePermissions = Object.fromEntries([
    ...knownPermissionEntries,
    ...tokenPermissionEntries,
  ]);

  return {
    ...basePermissions,
    ...overrides,
  };
};

export const PermissionsPanel = () => {
  const { overrides, setOverrides, showOverridesOnly } = useDeveloperToolsProvider();
  const { tokenProperties } = useClientTokenManager();
  const { permissions } = tokenProperties;
  const [newPermissionName, setNewPermissionName] = useState<string>('');
  const [searchString, setSearchString] = useState<string | undefined>('');

  const currentPermissions = getCurrentPermissions(
    showOverridesOnly,
    permissions,
    overrides.permissions,
  );
  const filteredPermissions = filterBySearch(currentPermissions, searchString);
  const filteredPermissionsEntries = Object.entries(filteredPermissions);

  const permissionPrefixes = filteredPermissionsEntries
    .map(([permission]) => permission.split(':')[0])
    .filter((item, index, arr) => arr.indexOf(item) === index);

  const togglePermission = (permission: string) => {
    setOverrides({
      ...overrides,
      permissions: {
        ...overrides.permissions,
        [permission]: !Boolean(currentPermissions[permission]),
      },
    });
  };

  const handleAddPermission = () => {
    if (newPermissionName.trim()) {
      setOverrides({
        ...overrides,
        permissions: { ...overrides.permissions, [newPermissionName]: true },
      });
    }
    setNewPermissionName('');
  };

  const gridViewContent = permissionPrefixes.map((prefix: string) => {
    const matchingPermissions = filteredPermissionsEntries.filter(([key]) =>
      key.startsWith(prefix),
    );
    return (
      <GridViewContent key={prefix} title={prefix}>
        {matchingPermissions.map(([permission]) => {
          return (
            <PermissionToggle
              key={permission}
              label={permission}
              value={Boolean(filteredPermissions[permission])}
              initialValue={permissions.includes(permission)}
              onToggle={() => togglePermission(permission)}
            />
          );
        })}
      </GridViewContent>
    );
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--layout-soybean)' }}>
      <TextHeadingTwo>Current Permissions</TextHeadingTwo>
      <div style={{ width: '400px' }}>
        <SearchBox
          aria-label="Search"
          placeholder="Search for a permission"
          onChange={setSearchString}
          onClear={() => setSearchString(undefined)}
          onSearch={() => {}}
        />
      </div>
      <GridView>{gridViewContent}</GridView>

      <TextHeadingThree>Add New Permission</TextHeadingThree>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: '300px 50px 50px',
          gap: '4px',
          alignItems: 'center',
        }}
      >
        <div style={{ width: '290px' }}>
          <Input
            label=""
            isRequired
            onChange={(e) => setNewPermissionName(e.target.value)}
            placeholder="Permission name"
            value={newPermissionName}
          />
        </div>

        <SecondaryButton onClick={handleAddPermission}>Add</SecondaryButton>
      </Box>
    </div>
  );
};
